import { useGenericMutation, type TMutationComposableParams } from "./useGenericMutation";
import type { ESubjects } from "@/api/models/auth/ability";
import type { QueryKey } from "@tanstack/vue-query";
import type { AxiosResponse } from "axios";

// ─── Simple CRUD ────────────────────────────────────────────────────────────

export interface ICrudService<
  TData,
  TCreateData = TData,
  TUpdateData extends { id?: string } = Partial<TData>,
> {
  create: (data: TCreateData) => Promise<TData> | Promise<AxiosResponse<TData>>;
  update: (id: string, data: TUpdateData) => Promise<TData> | Promise<AxiosResponse<TData>>;
  remove: (id: string) => Promise<void> | Promise<AxiosResponse<void>>;
}

// ─── Shared types ────────────────────────────────────────────────────────────

type TRemovesValue<TVariables> = QueryKey[] | ((variables: TVariables) => QueryKey[]);

export interface ICrudInvalidates<
  TCreateVars = string,
  TUpdateVars = string,
  TDeleteVars = string,
> {
  all?: QueryKey[];
  create?: TRemovesValue<TCreateVars>;
  update?: TRemovesValue<TUpdateVars>;
  delete?: TRemovesValue<TDeleteVars>;
}

export interface ICrudRemoves<TCreateVars, TUpdateVars, TDeleteVars = string> {
  all?: QueryKey[];
  create?: TRemovesValue<TCreateVars>;
  update?: TRemovesValue<TUpdateVars>;
  delete?: TRemovesValue<TDeleteVars>;
}

export interface ICrudConfig<
  TCreateData = any,
  TUpdateData extends { id?: string } = any,
  TDeleteData = string,
> {
  entityName: string; // e.g., "practice-staff"
  permissionSubject: ESubjects;
  invalidates: QueryKey[] | ICrudInvalidates;
  removes?: ICrudRemoves<TCreateData, TUpdateData, TDeleteData>;
}

const resolveInvalidates = <T>(
  invalidates: QueryKey[] | ICrudInvalidates<any, any, any>,
  op: keyof Omit<ICrudInvalidates, "all">
): TRemovesValue<T> | undefined => {
  if (Array.isArray(invalidates)) return invalidates;
  return (invalidates[op] as TRemovesValue<T> | undefined) ?? invalidates.all;
};

// ─── Simple CRUD mutations ───────────────────────────────────────────────────

export function createCrudMutations<
  TData,
  TCreateData = TData,
  TUpdateData extends { id?: string } = Partial<TData>,
>(
  service: ICrudService<TData, TCreateData, TUpdateData>,
  config: ICrudConfig<TCreateData, TUpdateData>
) {
  const useCreate = (params?: TMutationComposableParams<TCreateData>) => {
    return useGenericMutation<TData, TCreateData>({
      mutationFn: async (data: TCreateData) => service.create(data),
      feature: `create-${config.entityName}`,
      invalidates: resolveInvalidates(config.invalidates, "create"),
      removes: config.removes?.create ?? config.removes?.all,
      params: {
        permissions: {
          create: config.permissionSubject,
        },
        ...params,
      },
    });
  };

  const useUpdate = (params?: TMutationComposableParams<TUpdateData>) => {
    return useGenericMutation<TData, TUpdateData>({
      mutationFn: async (data: TUpdateData) => service.update(data.id!, data),
      feature: `update-${config.entityName}`,
      invalidates: resolveInvalidates(config.invalidates, "update"),
      removes: config.removes?.update ?? config.removes?.all,
      params: {
        permissions: {
          update: config.permissionSubject,
        },
        neededKey: ["id"],
        ...params,
      },
    });
  };

  const useDelete = (params?: TMutationComposableParams<string>) => {
    return useGenericMutation<void, string>({
      mutationFn: async (id: string) => service.remove(id),
      feature: `delete-${config.entityName}`,
      invalidates: resolveInvalidates(config.invalidates, "delete"),
      removes: config.removes?.delete ?? config.removes?.all,
      params: {
        permissions: {
          delete: config.permissionSubject,
        },
        ...params,
      },
    });
  };

  return { useCreate, useUpdate, useDelete };
}

// ─── Path-param CRUD mutations ───────────────────────────────────────────────

export interface IPathParamCrudService<
  TData,
  TPathParams extends Record<string, string | number>,
  TCreateData = Partial<TData>,
  TUpdateData = Partial<TData>,
  TId extends string | number = string,
> {
  create: (
    params: TPathParams,
    payload: TCreateData
  ) => Promise<TData> | Promise<AxiosResponse<TData>>;
  update: (
    params: TPathParams & { id?: TId },
    payload: TUpdateData
  ) => Promise<TData> | Promise<AxiosResponse<TData>>;
  remove: (params: TPathParams & { id?: TId }) => Promise<void> | Promise<AxiosResponse<void>>;
}

export function createPathParamCrudMutations<
  TData,
  TPathParams extends Record<string, string | number>,
  TCreateData = Partial<TData>,
  TUpdateData extends { id?: string } = Partial<TData>,
  TId extends string | number = string,
>(
  service: IPathParamCrudService<TData, TPathParams, TCreateData, TUpdateData, TId>,
  pathParamKeys: (keyof TPathParams)[],
  config: ICrudConfig<
    TPathParams & TCreateData,
    TPathParams & { id?: TId } & TUpdateData,
    TPathParams & { id?: TId }
  >
) {
  type TCreateVars = TPathParams & TCreateData;
  type TUpdateVars = TPathParams & { id?: TId } & TUpdateData;
  type TDeleteVars = TPathParams & { id?: TId };

  const extractPathParams = (variables: Record<string, any>): TPathParams => {
    const result = {} as TPathParams;
    pathParamKeys.forEach((key) => {
      (result as any)[key] = variables[key as string];
    });
    return result;
  };

  const useCreate = (params?: TMutationComposableParams<TCreateVars>) => {
    return useGenericMutation<TData, TCreateVars>({
      mutationFn: async (variables) => {
        const pathParams = extractPathParams(variables as Record<string, any>);
        return service.create(pathParams, variables as unknown as TCreateData);
      },
      feature: `create-${config.entityName}`,
      invalidates: resolveInvalidates(config.invalidates, "create"),
      removes: config.removes?.create ?? config.removes?.all,
      params: {
        permissions: { create: config.permissionSubject },
        ...params,
      },
    });
  };

  const useUpdate = (params?: TMutationComposableParams<TUpdateVars>) => {
    return useGenericMutation<TData, TUpdateVars>({
      mutationFn: async (variables) => {
        const pathParams = {
          ...extractPathParams(variables as Record<string, any>),
          id: variables.id,
        };
        return service.update(pathParams, variables as unknown as TUpdateData);
      },
      feature: `update-${config.entityName}`,
      invalidates: resolveInvalidates(config.invalidates, "update"),
      removes: config.removes?.update ?? config.removes?.all,
      params: {
        permissions: { update: config.permissionSubject },
        neededKey: ["id"],
        ...params,
      },
    });
  };

  const useDelete = (params?: TMutationComposableParams<TDeleteVars>) => {
    return useGenericMutation<void, TDeleteVars>({
      mutationFn: async (variables) => {
        const pathParams = {
          ...extractPathParams(variables as Record<string, any>),
          id: variables.id,
        };
        return service.remove(pathParams);
      },
      feature: `delete-${config.entityName}`,
      invalidates: resolveInvalidates(config.invalidates, "delete"),
      removes: config.removes?.delete ?? config.removes?.all,
      params: {
        permissions: { delete: config.permissionSubject },
        neededKey: ["id"],
        ...params,
      },
    });
  };

  return { useCreate, useUpdate, useDelete };
}
